import {gql} from '@apollo/client';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {LoadingState} from 'models/reduceLoadingState';
import ListingSettingsDropdown from 'nav/ListingSettingsDropdown';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import {getSortItemSectionsForSearchParams} from 'nav/sidepanel/header/SidepanelListingSettingsDropdown';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {AVAILABLE_PAGE_SIZES} from 'nav/sidepanel/PageSizeUtil';
import ClearSearchButton from 'search/ClearSearchButton';
import QueryInput from 'search/QueryInput';
import {
  FilterKey,
  LISTING_ERROR_MESSAGE,
  SearchParams,
  SearchType,
  Sort,
  SortKey,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ResultsPager from 'ui-library/ResultsPager';

import {WorkflowSummaryGraphQL} from '../../graphql-fragments/workflows/__generated/WorkflowSummaryGraphQL';
import {WorkflowDashboardQuery, WorkflowDashboardQueryVariables} from './__generated/WorkflowDashboardQuery';
import WorkflowTable from './components/WorkflowTable';
import {WorkflowSelector} from './reduceWorkflows';
import {getWorkflowsBaseUrl} from './util/WorkflowUtil';
import {WorkflowDashboardDetails, WorkflowTemplate} from './WorkflowConstants';
import WorkflowsFiltersContainer from './WorkflowsFiltersContainer';
import {getDefaultWorkflowsSearchParams} from './WorkflowsSearchParams';

interface EnhancedProps {
  workflowIdToDashboardDetails: {[workflowId: string]: WorkflowDashboardDetails} | null;
  workflowTemplates: Array<WorkflowTemplate> | null;
  isLoading: boolean;
}

class WorkflowDashboardScreenConnector extends DataConnector<SearchProps, EnhancedProps> {
  override renderLoading(): JSX.Element {
    return (
      <QueryingWorkflowDashboardScreen
        {...this.props}
        workflowIdToDashboardDetails={null}
        workflowTemplates={null}
        isLoading={true}
      />
    );
  }
  enhanceProps(): EnhancedProps {
    const props = {
      workflows: null,
      workflowIdToDashboardDetails: null,
      workflowTemplates: null,
      isLoading: true,
    };
    if (!this.props.searchResults) {
      return props;
    }
    const state = this.reduxState();
    const workflowSelector = new WorkflowSelector(state.models.workflows);
    const workflowIdToDashboardDetails = this.getOrFetchMultipleRequired(
      ManagedModelType.WORKFLOW_DASHBOARD_DETAILS,
      this.props.searchResults.ids
    );
    const workflowTemplates = workflowSelector.getAllWorkflowTemplates();
    return {
      ...props,
      workflowIdToDashboardDetails,
      workflowTemplates,
      isLoading: false,
    };
  }
}

export const WORKFLOW_DASHBOARD_QUERY = gql`
  query WorkflowDashboardQuery($workflowIds: [StrictString!]!) @skipAudit @engComponent(name: REQUESTS) {
    workflows(ids: $workflowIds) {
      ...WorkflowSummaryGraphQL
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('WorkflowSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function QueryingWorkflowDashboardScreen(props: SearchProps & EnhancedProps): JSX.Element {
  const workflowSummariesResponse = useSkippableBenchlingQuery<
    WorkflowDashboardQuery,
    WorkflowDashboardQueryVariables
  >(WORKFLOW_DASHBOARD_QUERY, {
    skip: !props.searchResults,
    variables: {workflowIds: props.searchResults?.ids ?? []},
  });

  if (!props.searchResults) {
    return <WorkflowDashboardScreen {...props} workflows={null} />;
  }

  return (
    <WorkflowDashboardScreen
      {...props}
      workflows={
        workflowSummariesResponse.type === QueryResultType.SUCCESS
          ? assertAllAccessible(workflowSummariesResponse.data.workflows)
          : null
      }
      isLoading={props.isLoading || workflowSummariesResponse.type === QueryResultType.PENDING}
    />
  );
}

export class WorkflowDashboardScreen extends React.Component<
  SearchProps & EnhancedProps & {workflows: Array<WorkflowSummaryGraphQL> | null}
> {
  _handleSearch = (searchParams: SearchParams): void => {
    this.props.onSearch(searchParams);
    SearchPreferences.rememberAppliedSortsToUserPreference(
      NavConstants.Section.WORKFLOWS,
      searchParams.sorts
    );
  };

  _handleSetSort = (sort: Sort): void => {
    this.props.onUpdateSortOrder([sort]);
  };

  _handleClear = (): void => {
    this._handleSearch(
      SearchUtil.clearSearchParams(this.props.searchParams, {preservedFilterKeys: [FilterKey.TYPES]})
    );
  };

  _getEmptyMessage = (): string =>
    this.props.loadingState === LoadingState.FAILED ? LISTING_ERROR_MESSAGE : 'No workflows';

  override render(): JSX.Element {
    const pagination = {
      offset: this.props.searchParams.offset,
      limit: this.props.searchParams.limit,
      total: this.props.searchResults ? this.props.searchResults.total : 0,
      isExact: this.props.searchResults ? this.props.searchResults.isExact : true,
    };
    return (
      <div className="u-flex-full-height">
        <div className="u-flex-full-height">
          <ListingHeader
            isExpanded={true}
            queryInput={
              <QueryInput
                onUpdateQuery={this.props.onUpdateQueryString}
                query={this.props.searchParams.query}
              />
            }
            filtersContainer={
              <WorkflowsFiltersContainer
                filters={this.props.searchParams.filters}
                filterErrors={this.props.filterErrors}
                onChangeFilters={this.props.onUpdateFilters}
                workflowTemplates={this.props.workflowTemplates}
                clearButton={
                  SearchUtil.hasQueryOrNonDefaultFilters(
                    this.props.searchParams,
                    [],
                    [ModelTypeConstants.WORKFLOW]
                  ) ? (
                    <ClearSearchButton onClear={this._handleClear} />
                  ) : null
                }
              />
            }
          />
          <div className="u-flex-display">
            <ResultsPager
              isLoading={!this.props.searchResults}
              setOffset={this.props.onUpdateOffset}
              pagination={pagination}
            />
            <ListingSettingsDropdown
              sortConfig={{
                availableSortOptions: getSortItemSectionsForSearchParams(this.props.searchParams, [
                  SortKey.SORT_NAME_KEY,
                  SortKey.SORT_CREATED_AT_KEY,
                  SortKey.SORT_IDENTIFIER_KEY,
                ]),
                sorts: this.props.searchParams.sorts,
                onSetSort: (sortKey: string, reverse: boolean) => {
                  this._handleSetSort({key: sortKey, reverse});
                },
              }}
              pageSizeConfig={{
                pageSize: this.props.searchParams.limit,
                availablePageSizes: AVAILABLE_PAGE_SIZES,
                onSetPageSize: this.props.onUpdatePageSize,
              }}
            />
          </div>
          {!this.props.isLoading &&
          this.props.workflows &&
          this.props.workflowTemplates &&
          this.props.workflowIdToDashboardDetails &&
          this.props.workflows.every(
            // @ts-expect-error -- line above checks for existence of workflowIdToDashboardDetails
            (workflow) => this.props.workflowIdToDashboardDetails[workflow.id] != null
          ) ? (
            <WorkflowTable
              workflows={this.props.workflows}
              workflowIdToDashboardDetails={this.props.workflowIdToDashboardDetails}
              emptyMessage={this._getEmptyMessage()}
              searchResults={this.props.searchResults}
              sorts={this.props.searchParams.sorts}
              onSetColumnHeaderSort={this._handleSetSort}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </div>
      </div>
    );
  }
}

function getWorkflowsURL(prevProps: SearchProps, props: SearchProps): NavigationUpdate {
  return {
    url: getNavigationURL(getWorkflowsBaseUrl(), props.searchParams, props.filterErrors),
  };
}

export default withSearching(
  WorkflowDashboardScreenConnector.connect(QueryingWorkflowDashboardScreen),
  SearchType.WORKFLOW,
  NavConstants.Section.WORKFLOWS,
  {defaultSearchParams: getDefaultWorkflowsSearchParams(), getNavigationUpdate: getWorkflowsURL}
);
